'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi } from '@/lib/api/auth';

/**
 * Страница запроса сброса пароля
 */
export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  /**
   * Валидация email
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Обработка отправки формы
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email обязателен');
      return;
    }

    if (!validateEmail(email)) {
      setError('Некорректный формат email');
      return;
    }

    setLoading(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch {
      // Всегда показываем успех для безопасности
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-card">
        <CardHeader className="text-center space-y-3 p-0">
          <CardTitle className="text-xl font-bold text-foreground">Проверьте почту</CardTitle>
        </CardHeader>
        <CardContent className="p-0 pt-6 text-center space-y-4">
          <p className="text-muted-foreground">
            Если email <strong>{email}</strong> зарегистрирован в системе, вы получите письмо со ссылкой для сброса пароля.
          </p>
          <p className="text-sm text-muted-foreground">
            Ссылка действительна в течение 1 часа.
          </p>
          <Link href="/login">
            <Button className="w-full mt-4">
              Вернуться на страницу входа
            </Button>
          </Link>
        </CardContent>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <CardHeader className="text-center space-y-3 p-0">
        <CardTitle className="text-xl font-bold text-foreground">Сброс пароля</CardTitle>
        <p className="text-muted-foreground text-sm">
          Введите email, указанный при регистрации. Мы отправим вам ссылку для сброса пароля.
        </p>
      </CardHeader>
      <CardContent className="p-0 pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              placeholder="example@mail.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              error={error}
              autoComplete="email"
            />

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Отправка…' : 'Отправить ссылку'}
            </Button>

            <Link
              href="/login"
              className="flex items-center justify-center gap-2 pt-2 text-sm text-primary hover:underline"
            >
              <ArrowLeft size={16} />
              Вернуться на страницу входа
            </Link>
          </form>
        </CardContent>
      </div>
  );
}
